/**
 * OperativeNotesForm.js
 * Structured operative notes form capturing procedure details, findings,
 * complications, specimens, and post-op instructions.
 */
import React, { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import TextField from '../../Utils/FormInputs/TextField';
import SelectInput from '../../Utils/FormInputs/SelectInput';
import Label from '../../Utils/FormInputs/Label';
import { ResizableTextarea } from '../../Utils/FormInputs/Textarea';
import { ANAESTHESIA_TYPE_LABELS } from '../shared/constants';
import { surgeryService } from '../../../services/surgeryService';

const POSITION_OPTIONS = [
  { value: '', text: 'Select position' },
  { value: 'supine', text: 'Supine' },
  { value: 'prone', text: 'Prone' },
  { value: 'lateral', text: 'Lateral' },
  { value: 'lithotomy', text: 'Lithotomy' },
  { value: 'sitting', text: 'Sitting' },
  { value: 'trendelenburg', text: 'Trendelenburg' },
  { value: 'lateral_decubitus', text: 'Lateral Decubitus' },
];

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 28px',
    borderRadius: 8,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#212121',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: '0.82rem',
    color: '#757575',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: '1px solid #F5F5F5',
  },
  anaesthesiaChip: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#E3F2FD',
    borderRadius: 4,
    fontSize: '0.82rem',
    fontWeight: 500,
    color: '#1565C0',
  },
  charCount: {
    fontSize: '0.7rem',
    color: '#BDBDBD',
    textAlign: 'right',
    marginTop: 2,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 16,
    borderTop: '1px solid #EEEEEE',
    marginTop: 8,
  },
}));

const OperativeNotesForm = ({ surgeryId, surgeryData = {} }) => {
  const classes = useStyles();

  // Fields
  const [procedurePerformed, setProcedurePerformed] = useState(surgeryData.procedure || '');
  const [indication, setIndication] = useState(surgeryData.diagnosis || '');
  const [surgeon, setSurgeon] = useState(surgeryData.surgeon_name || '');
  const [assistants, setAssistants] = useState('');
  const [anaesthetist, setAnaesthetist] = useState(surgeryData.anaesthetist_name || '');
  const [anaesthesiaType] = useState(surgeryData.anaesthesia_type || '');
  const [position, setPosition] = useState('supine');
  const [skinPrep, setSkinPrep] = useState('');
  const [incision, setIncision] = useState('');
  const [findings, setFindings] = useState('');
  const [procedureDetails, setProcedureDetails] = useState('');
  const [complications, setComplications] = useState('');
  const [specimens, setSpecimens] = useState('');
  const [drains, setDrains] = useState('');
  const [closure, setClosure] = useState('');
  const [estimatedBloodLoss, setEstimatedBloodLoss] = useState('');
  const [postOpInstructions, setPostOpInstructions] = useState('');

  // UI
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!surgeryId) return;
    surgeryService.getOperativeNote(surgeryId)
      .then((res) => {
        const d = res?.data;
        if (!d || typeof d !== 'object') return;
        if (d.procedure_performed) setProcedurePerformed(d.procedure_performed);
        if (d.indication) setIndication(d.indication);
        if (d.surgeon) setSurgeon(d.surgeon);
        if (d.assistants) setAssistants(d.assistants);
        if (d.anaesthetist) setAnaesthetist(d.anaesthetist);
        if (d.position) setPosition(d.position);
        if (d.skin_prep) setSkinPrep(d.skin_prep);
        if (d.incision) setIncision(d.incision);
        if (d.findings) setFindings(d.findings);
        if (d.procedure_details) setProcedureDetails(d.procedure_details);
        if (d.complications) setComplications(d.complications);
        if (d.specimens) setSpecimens(d.specimens);
        if (d.drains) setDrains(d.drains);
        if (d.closure) setClosure(d.closure);
        if (d.estimated_blood_loss != null) setEstimatedBloodLoss(String(d.estimated_blood_loss));
        if (d.post_op_instructions) setPostOpInstructions(d.post_op_instructions);
      })
      .catch(() => {});
  }, [surgeryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const setters = {
      procedure_performed: setProcedurePerformed,
      indication: setIndication,
      surgeon: setSurgeon,
      assistants: setAssistants,
      anaesthetist: setAnaesthetist,
      position: setPosition,
      skin_prep: setSkinPrep,
      incision: setIncision,
      findings: setFindings,
      procedure_details: setProcedureDetails,
      complications: setComplications,
      specimens: setSpecimens,
      drains: setDrains,
      closure: setClosure,
      estimated_blood_loss: setEstimatedBloodLoss,
      post_op_instructions: setPostOpInstructions,
    };
    if (setters[name]) setters[name](value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!procedurePerformed.trim() || !surgeon.trim() || !findings.trim() || !procedureDetails.trim()) return;

    setSaving(true);
    try {
      await surgeryService.saveOperativeNotes({
        surgery_id: surgeryId,
        procedure_performed: procedurePerformed.trim(),
        indication: indication.trim(),
        surgeon: surgeon.trim(),
        assistants: assistants.trim(),
        anaesthetist: anaesthetist.trim(),
        anaesthesia_type: anaesthesiaType,
        position,
        skin_prep: skinPrep.trim(),
        incision: incision.trim(),
        findings: findings.trim(),
        procedure_details: procedureDetails.trim(),
        complications: complications.trim() || null,
        specimens: specimens.trim() || null,
        drains: drains.trim() || null,
        closure: closure.trim(),
        estimated_blood_loss: estimatedBloodLoss ? Number(estimatedBloodLoss) : null,
        post_op_instructions: postOpInstructions.trim(),
      });
      setSnackbar({ open: true, message: 'Operative notes saved successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save operative notes', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper className={classes.root} elevation={1}>
      <div className={classes.header}>
        <Typography className={classes.title}>Operative Notes</Typography>
        <Typography className={classes.subtitle}>
          Surgery #{surgeryId} -- Complete operative documentation
        </Typography>
      </div>

      <form onSubmit={handleSubmit} autoComplete="off">
        {/* Procedure & Team */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Procedure & Team</Typography>

          <Form.Group>
            <Label name="Procedure Performed" type />
            <TextField
              name="procedure_performed"
              value={procedurePerformed}
              onChange={handleChange}
              type="text"
              submitted={submitted}
              placeholder="e.g. Laparoscopic Cholecystectomy"
            />
          </Form.Group>

          <Form.Group>
            <Label name="Indication" />
            <TextField
              name="indication"
              value={indication}
              onChange={handleChange}
              type="text"
              placeholder="e.g. Symptomatic gallstones"
            />
          </Form.Group>

          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Surgeon" type />
                <TextField
                  name="surgeon"
                  value={surgeon}
                  onChange={handleChange}
                  type="text"
                  submitted={submitted}
                  placeholder="e.g. Dr. Kizza"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Assistant(s)" />
                <TextField
                  name="assistants"
                  value={assistants}
                  onChange={handleChange}
                  type="text"
                  placeholder="e.g. Dr. Mugisha"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Anaesthetist" />
                <TextField
                  name="anaesthetist"
                  value={anaesthetist}
                  onChange={handleChange}
                  type="text"
                  placeholder="e.g. Dr. Nalwanga"
                />
              </Form.Group>
            </Col>
            {anaesthesiaType && (
              <Col xs={12} md={6}>
                <Form.Group>
                  <Label name="Anaesthesia Type" />
                  <span className={classes.anaesthesiaChip}>
                    {ANAESTHESIA_TYPE_LABELS[anaesthesiaType] || anaesthesiaType}
                  </span>
                </Form.Group>
              </Col>
            )}
          </Row>
        </div>

        {/* Operative Details */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Operative Details</Typography>

          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Position" />
                <SelectInput
                  name="position"
                  value={position}
                  onChange={handleChange}
                  options={POSITION_OPTIONS}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Skin Preparation" />
                <TextField
                  name="skin_prep"
                  value={skinPrep}
                  onChange={handleChange}
                  type="text"
                  placeholder="e.g. Povidone-iodine"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group>
            <Label name="Incision" />
            <TextField
              name="incision"
              value={incision}
              onChange={handleChange}
              type="text"
              placeholder="e.g. 4-port standard laparoscopic access"
            />
          </Form.Group>

          <Form.Group>
            <Label name="Findings" type />
            <ResizableTextarea
              rows={3}
              name="findings"
              value={findings}
              onChange={handleChange}
              submitted={submitted}
              placeholder="Describe intra-operative findings..."
            />
            <span className={classes.charCount}>{findings.length} characters</span>
          </Form.Group>

          <Form.Group>
            <Label name="Procedure Details" type />
            <ResizableTextarea
              rows={5}
              name="procedure_details"
              value={procedureDetails}
              onChange={handleChange}
              submitted={submitted}
              placeholder="Step-by-step description of the surgical procedure..."
            />
            <span className={classes.charCount}>{procedureDetails.length} characters</span>
          </Form.Group>
        </div>

        {/* Outcomes & Closure */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Outcomes & Closure</Typography>

          <Form.Group>
            <Label name="Complications" />
            <ResizableTextarea
              rows={2}
              name="complications"
              value={complications}
              onChange={handleChange}
              placeholder="None, or describe any intra-operative complications"
            />
          </Form.Group>

          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Specimens" />
                <TextField
                  name="specimens"
                  value={specimens}
                  onChange={handleChange}
                  type="text"
                  placeholder="e.g. Gallbladder sent for histology"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Drains" />
                <TextField
                  name="drains"
                  value={drains}
                  onChange={handleChange}
                  type="text"
                  placeholder="e.g. Jackson-Pratt drain"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Closure" />
                <TextField
                  name="closure"
                  value={closure}
                  onChange={handleChange}
                  type="text"
                  placeholder="e.g. Vicryl 2-0 subcuticular"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Blood Loss (ml)" />
                <TextField
                  name="estimated_blood_loss"
                  value={estimatedBloodLoss}
                  onChange={handleChange}
                  type="number"
                  placeholder="e.g. 150"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Post-Op Instructions */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Post-Operative Instructions</Typography>

          <Form.Group>
            <ResizableTextarea
              rows={4}
              name="post_op_instructions"
              value={postOpInstructions}
              onChange={handleChange}
              placeholder="Diet, mobilisation, medications, wound care, follow-up, warning signs..."
            />
          </Form.Group>
        </div>

        {/* Footer */}
        <div className={classes.footer}>
          <button
            type="submit"
            className="btn btn-sm sms-btn px-4"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Operative Notes'}
          </button>
        </div>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default OperativeNotesForm;
